import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerUser } from '../../services/AuthService';
import MobileAuthComingSoon from '../../components/mobile/MobileAuthComingSoon';
import { UsageEventsTracker } from '../../services/UsageEventsTracker';
import { isMobileAppReady } from '../../config/featureFlags';

/**
 * MobileRegisterPage
 * Premium, mobile-first registration page inspired by desktop Register design.
 * - Scout welcome panel
 * - Calm gradient background
 * - Validation + submit status
 * - No plan select (plans chosen in-app)
 * - Password-less verify-first flow
 * - Full ARIA support
 * - Analytics tracking
 * - "Already exists" error handling with modal
 */
export default function MobileRegisterPage() {
  const navigate = useNavigate();
  const SCOUT_SRC = '/scout_images/Scout_Thumbsup.png';

  // ====== VIEWPORT STATE ======
  const [isSmallPhone, setIsSmallPhone] = useState(window.innerWidth <= 390);

  React.useEffect(() => {
    const handleResize = () => setIsSmallPhone(window.innerWidth <= 390);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ====== FORM STATE ======
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [agree, setAgree] = useState(false);

  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    agree: false,
  });

  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(''); // friendly status text
  const [submitError, setSubmitError] = useState('');   // error text from server/validation
  const [accountExists, setAccountExists] = useState(false); // special "already exists" state
  const [showLoginModal, setShowLoginModal] = useState(false);

  // ====== VALIDATION ======
  const errors = {};
  const fn = firstName.trim();
  const ln = lastName.trim();
  const em = email.trim();

  if (!fn) errors.firstName = 'First name is required.';
  if (!ln) errors.lastName = 'Last name is required.';

  if (!em) {
    errors.email = 'Email is required.';
  } else {
    // basic email check
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em);
    if (!ok) errors.email = 'Please enter a valid email.';
  }

  if (!agree) errors.agree = 'Please agree to the Terms and Privacy Policy.';

  const hasErrors = Object.keys(errors).length > 0;

  function markTouched(key) {
    setTouched((prev) => ({ ...prev, [key]: true }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError('');
    setSubmitStatus('');
    setAccountExists(false);

    // Mark all touched so user sees errors immediately
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      agree: true,
    });

    if (hasErrors) return;

    setSubmitting(true);
    setSubmitStatus('Creating your account…');

    // Track registration attempt
    try {
      UsageEventsTracker.recordUserAction('REGISTRATION_ATTEMPT', {
        action: 'click',
        category: 'auth',
        entityType: 'button',
        label: 'Mobile Registration Submit',
        metadata: { email: email.trim(), platform: 'mobile' }
      });
    } catch (trackError) {
      console.warn('MobileRegisterPage: Failed to track registration attempt', trackError);
    }

    try {
      const regResult = await registerUser({
        fullName: `${firstName.trim()} ${lastName.trim()}`.trim(),
        email: email.trim(),
        password: undefined // password-less verify-first flow
      });

      if (!regResult.success) {
        // Check if account already exists
        const msg = (regResult.message || '').toLowerCase();
        if (msg.includes('already') || msg.includes('exist') || msg.includes('registered') || msg.includes('in use')) {
          setAccountExists(true);
          setSubmitError('An account with this email already exists.');
          
          // Track error with reason
          try {
            UsageEventsTracker.recordUserAction('REGISTRATION_ERROR', {
              action: 'error',
              category: 'auth',
              entityType: 'form',
              label: 'Mobile Registration Failed',
              metadata: { reason: 'already_exists', platform: 'mobile' }
            });
          } catch (trackError) {
            console.warn('MobileRegisterPage: Failed to track registration error', trackError);
          }
        } else {
          // Other registration error
          const friendly = regResult.message || 'Registration failed. Please try again.';
          setSubmitError(friendly);
          
          try {
            UsageEventsTracker.recordUserAction('REGISTRATION_ERROR', {
              action: 'error',
              category: 'auth',
              entityType: 'form',
              label: 'Mobile Registration Failed',
              metadata: { reason: 'validation', platform: 'mobile' }
            });
          } catch (trackError) {
            console.warn('MobileRegisterPage: Failed to track registration error', trackError);
          }
        }
        setSubmitting(false);
        return;
      }

      // Success! Track it
      try {
        UsageEventsTracker.recordUserAction('REGISTRATION_SUCCESS', {
          action: 'success',
          category: 'auth',
          entityType: 'form',
          label: 'Mobile Registration Success',
          metadata: { platform: 'mobile' }
        });
      } catch (trackError) {
        console.warn('MobileRegisterPage: Failed to track registration success', trackError);
      }

      setRegistered(true);

      // Check if auto-logged in and mobile app is ready
      if (regResult.autoLoggedIn && isMobileAppReady()) {
        setSubmitStatus('You are in! Redirecting...');
        setTimeout(() => {
          navigate('/m/courses');
        }, 650);
      } else {
        // Verify-first flow
        setSubmitStatus('Success! Check your email (and spam folder) to verify your account.');
      }
    } catch (err) {
      setSubmitStatus('');
      setSubmitError(err?.message || 'Something went wrong. Please try again.');
      
      try {
        UsageEventsTracker.recordUserAction('REGISTRATION_ERROR', {
          action: 'error',
          category: 'auth',
          entityType: 'form',
          label: 'Mobile Registration Failed',
          metadata: { reason: 'network', platform: 'mobile' }
        });
      } catch (trackError) {
        console.warn('MobileRegisterPage: Failed to track registration error', trackError);
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ====== STYLES (inline for single-file overwrite) ======
  const s = styles;

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div style={s.page}>
      <main style={s.main}>
        <div style={s.card}>
          {/* Scout Welcome (top) */}
          <section style={s.welcome}>
            <div style={s.mascotFrame}>
              <img 
                src={SCOUT_SRC} 
                alt="Scout welcoming you" 
                style={s.mascotImg}
                loading="lazy"
                decoding="async"
                onError={(ev) => {
                  ev.currentTarget.src = '/logos/blue_gold_head.png';
                }}
              />
            </div>

            <div style={s.welcomeTitle}>Welcome to the pack.</div>

            <div style={s.welcomeBody}>
              I've got you. Upload your class materials and DocuHounds builds your study HQ.
            </div>

            <div style={s.bullets}>
              <div style={s.bullet}>
                <span style={s.bulletIcon}>✓</span>
                <span>
                  <strong style={s.strong}>Upload</strong> docs and start studying fast
                </span>
              </div>
              <div style={s.bullet}>
                <span style={s.bulletIcon}>✓</span>
                <span>
                  <strong style={s.strong}>Organize</strong> by course, automatically
                </span>
              </div>
              <div style={s.bullet}>
                <span style={s.bulletIcon}>✓</span>
                <span>
                  <strong style={s.strong}>Study tools</strong> from your materials
                </span>
              </div>
            </div>

            <div style={s.trustPill}>
              <span aria-hidden="true">🔒</span>
              <span>Email verification • Privacy-first by default</span>
            </div>

            <div style={s.watermarkWrap} aria-hidden="true">
              <img 
                src={SCOUT_SRC} 
                alt="" 
                style={s.watermark}
                loading="lazy"
                decoding="async"
                onError={(ev) => {
                  ev.currentTarget.src = '/logos/blue_gold_head.png';
                }}
              />
            </div>
          </section>

          {/* Form (bottom) */}
          <section style={s.formSection}>
            <div style={s.formTitle}>Create your account</div>
            <div style={s.formSubtitle}>
              Scout sets up your study HQ and helps you stay in control all semester.
            </div>

            <form onSubmit={handleSubmit} style={s.form}>
              <div style={isSmallPhone ? s.rowStacked : s.row2}>
                <div style={s.field}>
                  <label style={s.label} htmlFor="firstName">First Name</label>
                  <input
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onBlur={() => markTouched('firstName')}
                    placeholder="First name"
                    style={{
                      ...s.input,
                      ...(touched.firstName && errors.firstName ? s.inputError : null),
                    }}
                    autoComplete="given-name"
                    disabled={registered || submitting}
                    aria-invalid={touched.firstName && errors.firstName ? 'true' : 'false'}
                    aria-describedby={touched.firstName && errors.firstName ? 'firstName-error' : undefined}
                  />
                  {touched.firstName && errors.firstName ? (
                    <div style={s.errorMsg} id="firstName-error" role="alert">{errors.firstName}</div>
                  ) : null}
                </div>

                <div style={s.field}>
                  <label style={s.label} htmlFor="lastName">Last Name</label>
                  <input
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onBlur={() => markTouched('lastName')}
                    placeholder="Last name"
                    style={{
                      ...s.input,
                      ...(touched.lastName && errors.lastName ? s.inputError : null),
                    }}
                    autoComplete="family-name"
                    disabled={registered || submitting}
                    aria-invalid={touched.lastName && errors.lastName ? 'true' : 'false'}
                    aria-describedby={touched.lastName && errors.lastName ? 'lastName-error' : undefined}
                  />
                  {touched.lastName && errors.lastName ? (
                    <div style={s.errorMsg} id="lastName-error" role="alert">{errors.lastName}</div>
                  ) : null}
                </div>
              </div>

              <div style={s.field}>
                <label style={s.label} htmlFor="email">Email Address</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => markTouched('email')}
                  placeholder="Enter your email"
                  style={{
                    ...s.input,
                    ...(touched.email && errors.email ? s.inputError : null),
                    ...(accountExists ? s.inputError : null),
                  }}
                  inputMode="email"
                  autoComplete="email"
                  disabled={registered || submitting}
                  aria-invalid={touched.email && errors.email ? 'true' : accountExists ? 'true' : 'false'}
                  aria-describedby={touched.email && errors.email ? 'email-error' : accountExists ? 'email-exists-error' : 'email-helper'}
                />
                {!registered && !accountExists && (
                  <div style={s.helper} id="email-helper">You'll set your password after verifying your email.</div>
                )}
                {touched.email && errors.email ? (
                  <div style={s.errorMsg} id="email-error" role="alert">{errors.email}</div>
                ) : null}
              </div>

              <div>
                <label style={s.checkRow}>
                  <div style={s.customCheckboxWrap}>
                    <input
                      type="checkbox"
                      checked={agree}
                      onChange={(e) => setAgree(e.target.checked)}
                      onBlur={() => markTouched('agree')}
                      style={s.checkboxInput}
                      id="agree"
                      disabled={registered || submitting}
                      aria-invalid={touched.agree && errors.agree ? 'true' : 'false'}
                      aria-describedby={touched.agree && errors.agree ? 'agree-error' : undefined}
                    />
                    <div 
                      style={agree ? {
                        width: 18,
                        height: 18,
                        borderRadius: 6,
                        border: '1.5px solid #2563eb',
                        background: '#2563eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all .15s ease',
                        boxShadow: '0 0 0 4px rgba(37,99,235,.10)',
                      } : {
                        width: 18,
                        height: 18,
                        borderRadius: 6,
                        border: '1.5px solid rgba(15,23,42,.22)',
                        background: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all .15s ease',
                      }}
                    >
                      {agree && <span style={{fontSize: 11, fontWeight: 900, color: '#fff'}}>✓</span>}
                    </div>
                  </div>
                  <span style={s.checkLabel}>
                    I agree to the{' '}
                    <Link to="/m/terms" style={s.link} onClick={(e) => e.stopPropagation()}>
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/m/privacy" style={s.link} onClick={(e) => e.stopPropagation()}>
                      Privacy Policy
                    </Link>
                  </span>
                </label>
                {touched.agree && errors.agree ? (
                  <div style={s.checkboxError} id="agree-error" role="alert" aria-live="assertive">{errors.agree}</div>
                ) : null}
              </div>

              {/* Primary CTA Button */}
              {!registered && (
                <button
                  type="submit"
                  disabled={submitting || hasErrors || !agree}
                  style={{
                    ...s.button,
                    ...(submitting || hasErrors || !agree ? s.buttonDisabled : null),
                  }}
                  aria-label={submitting ? 'Creating account' : 'Register your account'}
                >
                  {submitting ? (
                    <span style={s.buttonContent}>
                      <span style={s.spinner}>⟳</span>
                      Creating…
                    </span>
                  ) : (
                    'Register Account'
                  )}
                </button>
              )}

              {submitError && !accountExists ? (
                <div style={s.submitError} role="alert" aria-live="assertive">{submitError}</div>
              ) : null}

              {accountExists ? (
                <div style={s.accountExistsBox} role="alert" aria-live="assertive">
                  <div style={s.accountExistsMsg} id="email-exists-error">{submitError}</div>
                  <div style={s.accountExistsActions}>
                    <button
                      type="button"
                      onClick={() => setShowLoginModal(true)}
                      style={s.loginModalBtn}
                      aria-label="Try to login"
                    >
                      Try Login
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/m')}
                      style={s.backHomeBtn}
                      aria-label="Go back to homepage"
                    >
                      Back to homepage
                    </button>
                  </div>
                </div>
              ) : null}

              {submitStatus && !accountExists ? (
                <div style={s.submitStatus} role="status" aria-live="polite">
                  {submitStatus}
                  {registered && !isMobileAppReady() && (
                    <div style={s.spamHint}>(Don't forget to check spam!)</div>
                  )}
                </div>
              ) : null}

              {registered && !accountExists ? (
                <div style={s.successContainer}>
                  <div style={s.successMessage} role="status" aria-live="polite">
                    <div style={s.successIcon}>✓</div>
                    <div style={s.successTitle}>You're in!</div>
                    <div style={s.successBody}>
                      Check your email (and spam folder) to verify your account and get started.
                    </div>
                  </div>
                  <div style={s.successActions}>
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      style={s.primaryActionBtn}
                      aria-label="Back to Scout's homepage"
                    >
                      Back to Scout's homepage
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        navigate('/');
                        setTimeout(() => {
                          const pricing = document.getElementById('pricing');
                          if (pricing) pricing.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      }}
                      style={s.secondaryActionBtn}
                      aria-label="Continue browsing"
                    >
                      Continue browsing
                    </button>
                  </div>
                </div>
              ) : null}
            </form>
          </section>
        </div>
      </main>

      {/* Mobile Auth Coming Soon Modal */}
      <MobileAuthComingSoon 
        open={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
        mode="login" 
      />
    </div>
    </>
  );
}

// ====== Inline styles (keeps overwrite self-contained) ======
const styles = {
  page: {
    minHeight: '100svh', // Use svh for iOS Safari
    background:
      'radial-gradient(1200px 520px at 50% 8%, rgba(88,140,255,.22), transparent 55%), ' +
      'radial-gradient(900px 520px at 18% 86%, rgba(120,220,200,.14), transparent 60%), ' +
      'linear-gradient(180deg, #f4f8ff 0%, #eef3ff 60%, #edf7f6 100%)',
  },
  main: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px 12px 60px',
  },
  card: {
    width: '100%',
    maxWidth: 460,
    borderRadius: 22,
    overflow: 'visible',
    border: '1px solid rgba(35,55,95,.10)',
    boxShadow: '0 25px 70px rgba(19,39,67,.18)',
    background: 'var(--card-bg, rgba(255,255,255,.92))',
    minHeight: 'auto',
    height: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },

  // Welcome panel
  welcome: {
    position: 'relative',
    padding: '6px 12px 8px',
    textAlign: 'center',
    background:
      'radial-gradient(700px 500px at 50% 20%, rgba(88,140,255,.20), transparent 62%), ' +
      'linear-gradient(180deg, #eef3ff 0%, #eaf0ff 55%, #f1fbff 100%)',
    flex: '0 0 auto',
  },
  mascotFrame: {
    width: 76,
    height: 76,
    margin: '0 auto 5px',
    borderRadius: 16,
    background: 'rgba(255,255,255,.85)',
    border: '1px solid rgba(35,55,95,.10)',
    boxShadow: '0 6px 18px rgba(19,39,67,.12), 0 2px 6px rgba(19,39,67,.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mascotImg: {
    width: '88%',
    height: '88%',
    objectFit: 'contain',
  },
  welcomeTitle: {
    fontSize: 19,
    fontWeight: 900,
    letterSpacing: '-0.02em',
    color: '#1d4ed8',
    marginBottom: 5,
  },
  welcomeBody: {
    fontSize: 12.5,
    lineHeight: 1.3,
    color: 'rgba(17,24,39,.78)',
    maxWidth: 360,
    margin: '0 auto 8px',
  },
  bullets: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    maxWidth: 380,
    margin: '0 auto',
    textAlign: 'left',
  },
  bullet: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 7,
    fontSize: 12.5,
    color: 'rgba(17,24,39,.72)',
    lineHeight: 1.2,
  },
  bulletIcon: {
    width: 16,
    height: 16,
    borderRadius: 999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 900,
    color: '#fff',
    background: 'rgba(37,99,235,.95)',
    boxShadow: '0 0 0 4px rgba(37,99,235,.10)',
    marginTop: 2,
    flex: '0 0 auto',
  },
  strong: {
    color: 'rgba(17,24,39,.86)',
    fontWeight: 900,
  },
  trustPill: {
    margin: '8px auto 0',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '5px 9px',
    borderRadius: 999,
    background: 'rgba(255,255,255,.80)',
    border: '1px solid rgba(35,55,95,.10)',
    color: 'rgba(17,24,39,.60)',
    fontSize: 10.5,
  },
  watermarkWrap: {
    position: 'absolute',
    right: -32,
    bottom: -36,
    width: 220,
    height: 220,
    zIndex: 1,
    pointerEvents: 'none',
  },
  watermark: {
    width: '100%',
    height: '100%',
    opacity: 0.07,
    objectFit: 'contain',
    filter: 'saturate(.95)',
  },

  // Form section
  formSection: {
    position: 'relative',
    zIndex: 2,
    padding: '12px 16px 24px',
    background: '#fff',
    borderTop: '1px solid rgba(15,23,42,.07)',
    overflow: 'visible',
    minHeight: 'auto',
    height: 'auto',
    flex: '0 0 auto',
  },
  formTitle: {
    fontSize: 23,
    fontWeight: 900,
    letterSpacing: '-0.03em',
    color: 'rgba(15,23,42,.96)',
  },
  formSubtitle: {
    marginTop: 6,
    fontSize: 13,
    lineHeight: 1.35,
    color: 'rgba(15,23,42,.70)',
  },
  form: {
    marginTop: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    overflow: 'visible',
    height: 'auto',
  },
  row2: {
    display: 'flex',
    gap: 10,
    width: '100%',
    boxSizing: 'border-box',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  rowStacked: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%',
    boxSizing: 'border-box',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0',
    minWidth: 160,
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  label: {
    fontSize: 11,
    fontWeight: 800,
    color: 'rgba(15,23,42,.72)',
    marginBottom: 5,
  },
  input: {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    height: 44,
    borderRadius: 14,
    border: '1px solid rgba(35,55,95,.14)',
    background: '#f8fbff',
    padding: '0 14px',
    outline: 'none',
    fontSize: 14,
    color: 'rgba(15,23,42,.92)',
  },
  inputError: {
    borderColor: 'rgba(239,68,68,.85)',
    boxShadow: '0 0 0 6px rgba(239,68,68,.10)',
    background: 'rgba(255,245,245,.90)',
  },
  helper: {
    marginTop: 4,
    fontSize: 11,
    color: 'rgba(15,23,42,.52)',
  },
  checkRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    marginTop: 2,
  },
  checkboxInput: {
    position: 'absolute',
    opacity: 0,
    width: 0,
    height: 0,
  },
  customCheckboxWrap: {
    position: 'relative',
    marginTop: 2,
  },
  customCheckbox: {
    width: 18,
    height: 18,
    borderRadius: 6,
    border: '1.5px solid rgba(15,23,42,.22)',
    background: 'var(--card-bg, #fff)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all .15s ease',
  },
  customCheckboxChecked: {
    background: 'var(--action-primary-bg, #2563eb)',
    borderColor: 'var(--action-primary-bg, #2563eb)',
    boxShadow: '0 0 0 4px rgba(37,99,235,.10)',
  },
  checkmark: {
    fontSize: 11,
    fontWeight: 900,
    color: '#fff',
  },
  checkLabel: {
    fontSize: 12.5,
    lineHeight: 1.3,
    color: 'rgba(15,23,42,.68)',
  },
  link: {
    color: '#2563eb',
    textDecoration: 'none',
    fontWeight: 800,
  },
  errorMsg: {
    marginTop: 6,
    fontSize: 12,
    color: 'rgba(185,28,28,.92)',
    fontWeight: 700,
  },
  checkboxError: {
    marginTop: 6,
    fontSize: 12,
    color: 'rgba(185,28,28,.92)',
    fontWeight: 700,
  },
  submitError: {
    marginTop: 4,
    padding: '10px 12px',
    borderRadius: 14,
    background: 'rgba(254,242,242,.95)',
    border: '1px solid rgba(239,68,68,.25)',
    color: 'rgba(185,28,28,.92)',
    fontSize: 13,
    fontWeight: 700,
  },
  submitStatus: {
    marginTop: 4,
    padding: '10px 12px',
    borderRadius: 14,
    background: 'rgba(239,246,255,.95)',
    border: '1px solid rgba(37,99,235,.18)',
    color: 'rgba(15,23,42,.72)',
    fontSize: 13,
    fontWeight: 700,
  },
  spamHint: {
    marginTop: 6,
    fontSize: 12,
    color: 'rgba(15,23,42,.58)',
    fontWeight: 600,
  },
  accountExistsBox: {
    marginTop: 4,
    padding: '12px 14px',
    borderRadius: 14,
    background: 'rgba(254,242,242,.95)',
    border: '1px solid rgba(239,68,68,.25)',
  },
  accountExistsMsg: {
    fontSize: 13,
    color: 'rgba(185,28,28,.92)',
    fontWeight: 700,
    marginBottom: 10,
  },
  accountExistsActions: {
    display: 'flex',
    gap: 8,
  },
  loginModalBtn: {
    flex: 1,
    height: 42,
    borderRadius: 12,
    border: 'none',
    cursor: 'pointer',
    background: 'var(--action-primary-bg, rgba(37,99,235,1))',
    color: 'var(--action-primary-text, #fff)',
    fontSize: 14,
    fontWeight: 800,
    boxShadow: '0 8px 20px rgba(37,99,235,.22)',
  },
  backHomeBtn: {
    flex: 1,
    height: 42,
    borderRadius: 12,
    border: '1px solid rgba(35,55,95,.18)',
    cursor: 'pointer',
    background: 'var(--card-bg, #fff)',
    color: 'rgba(15,23,42,.82)',
    fontSize: 14,
    fontWeight: 700,
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    marginTop: 16,
    marginBottom: 24,
    height: 48,
    borderRadius: 14,
    border: 'none',
    cursor: 'pointer',
    background: 'var(--action-primary-bg, rgba(37,99,235,1))',
    color: 'var(--action-primary-text, #fff)',
    fontSize: 15,
    fontWeight: 900,
    letterSpacing: '0.01em',
    boxShadow: '0 4px 12px rgba(37,99,235,.28)',
    transition: 'transform .12s ease, box-shadow .12s ease',
    position: 'relative',
    zIndex: 10,
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    boxShadow: '0 2px 8px rgba(37,99,235,.14)',
  },
  buttonContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  spinner: {
    display: 'inline-block',
    animation: 'spin 1s linear infinite',
    fontSize: 16,
  },
  successContainer: {
    marginTop: 12,
  },
  successMessage: {
    padding: '18px 16px',
    borderRadius: 14,
    background: 'rgba(240,253,244,.95)',
    border: '1px solid rgba(34,197,94,.20)',
    textAlign: 'center',
  },
  successIcon: {
    width: 48,
    height: 48,
    margin: '0 auto 10px',
    borderRadius: 999,
    background: 'rgba(34,197,94,.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 24,
    fontWeight: 900,
    color: '#22c55e',
  },
  successTitle: {
    fontSize: 18,
    fontWeight: 900,
    color: 'rgba(15,23,42,.92)',
    marginBottom: 6,
  },
  successBody: {
    fontSize: 13,
    lineHeight: 1.4,
    color: 'rgba(15,23,42,.70)',
  },
  buttonSuccess: {
    background: 'rgba(34,197,94,.92)',
    boxShadow: '0 14px 30px rgba(34,197,94,.22)',
    cursor: 'default',
  },
  successActions: {
    marginTop: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  primaryActionBtn: {
    height: 46,
    borderRadius: 14,
    border: 'none',
    cursor: 'pointer',
    background: 'var(--action-primary-bg, rgba(37,99,235,1))',
    color: 'var(--action-primary-text, #fff)',
    fontSize: 14,
    fontWeight: 800,
    boxShadow: '0 12px 28px rgba(37,99,235,.24)',
  },
  secondaryActionBtn: {
    height: 42,
    borderRadius: 14,
    border: '1px solid rgba(35,55,95,.18)',
    cursor: 'pointer',
    background: 'var(--card-bg, #fff)',
    color: 'rgba(15,23,42,.82)',
    fontSize: 14,
    fontWeight: 700,
  },
};
